/**
 * OREF panel — Israeli Home Front Command sirens feed.
 *
 * Filters events where source === 'oref' and displays as a
 * real-time feed with location, alert type, timestamp.
 * Color-coded by recency: <5min red pulse, <30min orange, <1h yellow, older muted.
 */

import { appStore } from '@/core/store';
import { relativeTime } from '@/utils/time';
import { getMap } from '@/map/engine';

export function createOrefPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'oref-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function getRecencyColor(timestamp: string): string {
    const ageMs = Date.now() - new Date(timestamp).getTime();
    const fiveMin = 5 * 60 * 1000;
    const thirtyMin = 30 * 60 * 1000;
    const oneHour = 60 * 60 * 1000;

    if (ageMs < fiveMin) return 'var(--severity-critical)';
    if (ageMs < thirtyMin) return 'var(--severity-high)';
    if (ageMs < oneHour) return 'var(--severity-medium)';
    return 'var(--text-muted)';
  }

  function render(): void {
    const events = appStore.getState().events;
    const orefEvents = events
      .filter(e => e.source === 'oref')
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    contentArea.textContent = '';

    // Alert count in last hour
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    const recentCount = orefEvents.filter(e => new Date(e.timestamp).getTime() > oneHourAgo).length;

    const countHeader = document.createElement('div');
    countHeader.style.cssText = `
      display: flex; align-items: center; gap: 8px; margin-bottom: 12px;
      padding: 8px 10px; border-radius: 6px;
      background: var(--bg-secondary); border: 1px solid var(--border-primary);
    `;

    const countNum = document.createElement('span');
    countNum.style.cssText = `font-size: 20px; font-weight: 700; font-family: var(--font-mono); color: ${recentCount > 0 ? 'var(--severity-critical)' : 'var(--text-muted)'};`;
    countNum.textContent = String(recentCount);
    countHeader.appendChild(countNum);

    const countLabel = document.createElement('span');
    countLabel.style.cssText = 'font-size: 11px; color: var(--text-secondary);';
    countLabel.textContent = `alert${recentCount !== 1 ? 's' : ''} in last hour`;
    countHeader.appendChild(countLabel);

    contentArea.appendChild(countHeader);

    // Feed header
    const feedHeader = document.createElement('div');
    feedHeader.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em;';
    feedHeader.textContent = `Siren Feed (${orefEvents.length})`;
    contentArea.appendChild(feedHeader);

    if (orefEvents.length === 0) {
      const msg = document.createElement('div');
      msg.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic; padding: 16px 0; text-align: center;';
      msg.textContent = 'No OREF alerts received';
      contentArea.appendChild(msg);
      return;
    }

    for (const ev of orefEvents.slice(0, 100)) {
      const color = getRecencyColor(ev.timestamp);

      const row = document.createElement('div');
      row.style.cssText = `
        padding: 6px 8px; margin-bottom: 4px; border-radius: 4px;
        background: var(--bg-secondary); border-left: 3px solid ${color};
        cursor: pointer;
      `;

      // Click to fly to location
      if (ev.location) {
        row.onclick = () => {
          const map = getMap();
          if (map && ev.location) {
            map.flyTo({
              center: [ev.location.lng, ev.location.lat],
              zoom: Math.max(map.getZoom(), 10),
              duration: 600,
            });
          }
        };
        row.title = 'Click to fly to location';
      }

      // Location + type
      const titleRow = document.createElement('div');
      titleRow.style.cssText = 'display: flex; align-items: center; gap: 6px;';

      const locName = document.createElement('span');
      locName.style.cssText = `font-size: 12px; font-weight: 500; color: ${color};`;
      locName.textContent = ev.location?.name ?? ev.title;
      titleRow.appendChild(locName);

      if (ev.type && ev.type !== 'custom') {
        const typeBadge = document.createElement('span');
        typeBadge.style.cssText = 'font-size: 9px; color: var(--text-muted); margin-left: auto;';
        typeBadge.textContent = ev.type.replace(/_/g, ' ');
        titleRow.appendChild(typeBadge);
      }

      row.appendChild(titleRow);

      // Timestamp
      const time = document.createElement('div');
      time.style.cssText = 'font-size: 10px; color: var(--text-muted); margin-top: 2px;';
      time.textContent = relativeTime(ev.timestamp);
      row.appendChild(time);

      contentArea.appendChild(row);
    }
  }

  // Initial render
  render();

  // Subscribe to event changes
  cleanupFns.push(
    appStore.subscribe(
      (s) => s.events,
      () => scheduleRender(),
    ),
  );

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
